/**
 * Danh mục Đại lý / thương lái / tổ chức nông dân (MM) — master data thứ ba (Doc/09 lát 1).
 *
 * Khác màn Hộ ở một điểm: điều tra viên chỉ XEM. Ba phiếu RCP chọn MM có sẵn chứ không
 * tạo tại hiện trường (Doc/09 mục 8.4), nên nút thêm/sửa/xoá chỉ hiện với điều phối viên
 * trở lên — và việc chặn thật sự nằm ở `@Roles` của API, không ở việc ẩn nút (R7).
 * Thấy sai thì điều tra viên **báo sai** (đề xuất sửa, Doc/11) — người duyệt quyết.
 *
 * Như Hộ: tạo MM luôn cần mạng (ADR-6), không xếp hàng offline.
 */
import * as api from '../api-client.js';
import { MIDDLEMAN_TYPES, MIDDLEMAN_TYPE_LABELS, formLabel } from '@farmer/audit-rules';
import { toast, navigate, buildHash, debounce, el, esc, formatDateTime, statusBadge } from '../ui/util.js';
import { createRegionPicker } from '../ui/region-picker.js';
import { openFormDialog } from '../ui/form-dialog.js';
import { EDIT_REASON_FIELD_HTML, editReasonError, openDeleteDialog } from '../ui/edit-reason.js';
import { renderChangeLog } from '../ui/change-log.js';
import { openProposalSheet, loadOpenProposals, renderOpenProposals } from '../ui/proposal-sheet.js';

const MANAGER_ROLES = ['supervisor', 'instance_admin', 'admin'];

function canManage() {
  return MANAGER_ROLES.includes(api.getCurrentUser()?.role);
}

/** Nhãn tiếng Việt của loại MM; mã lạ (dữ liệu cũ hơn danh sách) hiện nguyên mã thay vì trống. */
export function middlemanTypeLabel(type) {
  return MIDDLEMAN_TYPE_LABELS[type] || type || '—';
}

function typeOptionsHtml(selected) {
  return MIDDLEMAN_TYPES.map(
    (t) => `<option value="${t}" ${t === selected ? 'selected' : ''}>${esc(MIDDLEMAN_TYPE_LABELS[t])}</option>`,
  ).join('');
}

/**
 * MM bắt buộc có XÃ, không chỉ tỉnh: "Tỉnh, Xã" là thông tin cơ bản, và máy chủ cần xã để
 * điều phối viên giới hạn theo địa bàn thấy được MM. `getValue()` của ô chọn địa bàn trả
 * về tỉnh khi chưa chọn xã, nên phải đọc thẳng ô xã.
 */
function pickedCommune(host) {
  return host.querySelector('[data-role="commune"]')?.value || '';
}

export async function renderMiddlemanList(container) {
  const manager = canManage();
  container.innerHTML = `
    <div class="page-head">
      <div><h1>Đại lý / MM</h1><p>Đại lý, thương lái, hợp tác xã, công ty, vườn ươm — đối tượng của phiếu tham vấn MM/FO.</p></div>
      ${manager ? `<div class="page-head-actions">
        <a class="btn btn-tonal" href="${buildHash('/middlemen/import')}"><span class="ms">upload_file</span>Nhập từ Excel</a>
        <button class="btn" id="toggleCreate"><span class="ms">add_business</span>Thêm MM</button>
      </div>` : ''}
    </div>
    <div class="card" id="createCard" hidden></div>
    <div class="filters-bar">
      <input type="search" id="searchInput" class="search-field" placeholder="Tìm theo tên / mã MM...">
      <select id="typeFilter" class="filters-select" aria-label="Loại MM">
        <option value="">Tất cả loại</option>${typeOptionsHtml('')}
      </select>
    </div>
    <div class="card">
      <table class="list-table">
        <thead><tr><th>Mã</th><th>Tên</th><th>Loại</th><th>Địa bàn</th><th>Địa chỉ</th></tr></thead>
        <tbody id="resultsBody"></tbody>
      </table>
      <div id="resultsEmpty" class="empty-state" hidden><span class="ms ms-xl">storefront</span><p>Chưa có MM nào khớp.</p></div>
      <div id="resultsPaging" class="paging"></div>
    </div>`;

  const toggle = container.querySelector('#toggleCreate');
  if (toggle && !navigator.onLine) {
    toggle.disabled = true;
    toggle.title = 'Cần có mạng để tạo hồ sơ mới.';
  }
  toggle?.addEventListener('click', () => {
    const card = container.querySelector('#createCard');
    card.hidden = !card.hidden;
    if (!card.hidden) {
      void renderCreateForm(card, {
        onCreated: (mm) => navigate(buildHash(`/middlemen/${mm.id}`)),
      });
    }
  });

  const searchInput = container.querySelector('#searchInput');
  const typeFilter = container.querySelector('#typeFilter');
  searchInput.addEventListener('input', debounce(() => search(1), 350));
  typeFilter.addEventListener('change', () => search(1));

  async function search(page) {
    const body = container.querySelector('#resultsBody');
    const empty = container.querySelector('#resultsEmpty');
    body.innerHTML = `<tr><td colspan="5">Đang tải...</td></tr>`;
    empty.hidden = true;
    try {
      const res = await api.middlemen.list({
        search: searchInput.value.trim(),
        type: typeFilter.value || undefined,
        page,
        pageSize: 20,
      });
      body.innerHTML = '';
      empty.hidden = res.data.length > 0;
      res.data.forEach((mm) => {
        const tr = el('tr');
        tr.innerHTML = `
          <td class="mono">${esc(mm.code)}</td>
          <td><a href="${buildHash(`/middlemen/${mm.id}`)}">${esc(mm.name)}</a></td>
          <td>${esc(middlemanTypeLabel(mm.type))}</td>
          <td>${esc(mm.regionName || '—')}</td>
          <td>${esc(mm.addressDetail || '—')}</td>`;
        body.appendChild(tr);
      });
      renderPaging(res.meta);
    } catch (err) {
      body.innerHTML = `<tr><td colspan="5">Lỗi: ${esc(err.message)}</td></tr>`;
    }
  }

  function renderPaging(meta) {
    const host = container.querySelector('#resultsPaging');
    host.innerHTML = '';
    if (meta.totalPages <= 1) return;
    const prev = el('button', 'btn btn-text sm', 'Trước');
    prev.disabled = meta.page <= 1;
    prev.addEventListener('click', () => search(meta.page - 1));
    const info = el('span', 'paging-info', `Trang ${meta.page}/${meta.totalPages} (${meta.total} MM)`);
    const next = el('button', 'btn btn-text sm', 'Sau');
    next.disabled = meta.page >= meta.totalPages;
    next.addEventListener('click', () => search(meta.page + 1));
    host.append(prev, info, next);
  }

  search(1);
}

/** Như màn Hộ: chỉ admin hệ thống phải chọn đơn vị — ba vai trò còn lại server tự lấy đơn vị của họ. */
async function loadInstancesForAdmin() {
  if (api.getCurrentUser()?.role !== 'admin') return null;
  try {
    return (await api.instances.list()).filter((d) => d.isActive);
  } catch (err) {
    console.warn('[middlemen] không tải được danh sách đơn vị', err);
    return [];
  }
}

async function renderCreateForm(host, { onCreated }) {
  const donVis = await loadInstancesForAdmin();
  host.innerHTML = `
    <h2 class="card-title"><span class="ms">add_business</span>Thêm Đại lý / MM</h2>
    <form id="createMiddlemanForm">
      <div class="form-grid">
        ${
          donVis
            ? `<div class="form-row"><label>Đơn vị điều tra *</label>
                 <select name="instanceId" required>
                   <option value="">— Chọn đơn vị —</option>
                   ${donVis.map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`).join('')}
                 </select>
                 <p class="hint-text">MM này chỉ hiện với người của đơn vị được chọn.</p></div>`
            : ''
        }
        <div class="form-row"><label>Mã MM *</label><input name="code" required maxlength="50">
          <p class="hint-text">Theo cơ sở dữ liệu của IDH. Không trùng trong đơn vị.</p></div>
        <div class="form-row"><label>Tên *</label><input name="name" required maxlength="200"></div>
        <div class="form-row"><label>Loại *</label>
          <select name="type" required><option value="">— Chọn loại —</option>${typeOptionsHtml('')}</select></div>
        <div id="regionPickerHost" class="contents"></div>
        <div class="form-row"><label>Địa chỉ</label><input name="addressDetail"></div>
      </div>
      <div class="error-text" id="createMiddlemanError" hidden></div>
      <div class="form-actions"><button class="btn" type="submit">Lưu MM</button></div>
    </form>`;

  const form = host.querySelector('#createMiddlemanForm');
  const errBox = host.querySelector('#createMiddlemanError');
  const regionHost = host.querySelector('#regionPickerHost');
  createRegionPicker(regionHost, { required: true });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    const fd = new FormData(form);
    const regionId = pickedCommune(regionHost);
    if (!regionId) {
      errBox.textContent = 'Chọn Tỉnh và Xã nơi MM đặt cơ sở.';
      errBox.hidden = false;
      return;
    }
    const payload = {
      ...(donVis ? { instanceId: fd.get('instanceId') || undefined } : {}),
      code: fd.get('code').trim(),
      name: fd.get('name').trim(),
      type: fd.get('type'),
      regionId,
      addressDetail: fd.get('addressDetail').trim() || undefined,
    };
    try {
      const mm = await api.middlemen.create(payload);
      toast('Đã thêm MM');
      onCreated(mm);
    } catch (err) {
      // Trùng mã: MM cần nhập đã có trong danh mục — đưa thẳng lối sang hồ sơ đó.
      if (err.code === 'CONFLICT' && err.existing?.id) {
        errBox.innerHTML = `${esc(err.message)} — <a href="${buildHash(`/middlemen/${err.existing.id}`)}">mở hồ sơ MM này</a>`;
      } else {
        errBox.textContent = err.message;
      }
      errBox.hidden = false;
    }
  });
}

export async function renderMiddlemanDetail(container, { id }) {
  container.innerHTML = `<div class="empty-state">Đang tải...</div>`;
  let mm;
  try {
    mm = await api.middlemen.get(id);
  } catch (err) {
    container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    return;
  }
  const manager = canManage();

  container.innerHTML = `
    <div class="page-head">
      <div><h1>${esc(mm.name)}</h1><p>Mã ${esc(mm.code)} · ${esc(middlemanTypeLabel(mm.type))}</p></div>
      <div class="page-head-actions">
        <a class="btn btn-text" href="${buildHash('/middlemen')}"><span class="ms">arrow_back</span>Danh mục MM</a>
        <a class="btn" href="${buildHash('/submissions/new', { middlemanId: mm.id })}"><span class="ms">note_add</span>Tạo phiếu tham vấn</a>
        <button class="btn btn-text" id="btnReport"><span class="ms">flag</span>Báo sai thông tin</button>
        ${manager ? `<button class="btn btn-tonal" id="btnEdit"><span class="ms">edit</span>Sửa hồ sơ</button>` : ''}
      </div>
    </div>
    <div class="two-col">
      <div>
        <div class="card proposals-card" id="proposalsCard" hidden></div>
        <div class="card">
          <h2 class="card-title"><span class="ms">history</span>Phiếu tham vấn MM/FO (${mm.submissions?.length ?? 0})</h2>
          ${mm.submissions?.length ? `<table class="list-table"><thead><tr><th>Loại phiếu</th><th>Trạng thái</th><th>Bắt đầu</th><th>Ngày nộp</th><th></th></tr></thead>
            <tbody>${mm.submissions.map((s) => `<tr>
                <td>${esc(formLabel(s.formKey))}</td>
                <td>${statusBadge(s.status)}</td>
                <td>${formatDateTime(s.startedAt)}</td>
                <td>${formatDateTime(s.submittedAt)}</td>
                <td><a href="${buildHash(`/submissions/${s.id}`)}">Mở</a></td>
              </tr>`).join('')}</tbody></table>`
            : `<p class="hint-text">Chưa có phiếu nào.</p>`}
        </div>
        <div class="card" id="changeLogCard">
          <h2 class="card-title"><span class="ms">manage_history</span>Lịch sử chỉnh sửa hồ sơ (US A3)</h2>
          <p class="hint-text">Đang tải...</p>
        </div>
      </div>
      <div class="card">
        <h2 class="card-title"><span class="ms">info</span>Thông tin MM</h2>
        <dl class="info-list">
          <div><dt>Mã</dt><dd class="mono">${esc(mm.code)}</dd></div>
          <div><dt>Loại</dt><dd>${esc(middlemanTypeLabel(mm.type))}</dd></div>
          <div><dt>Địa bàn</dt><dd>${esc(mm.regionName || '—')}</dd></div>
          <div><dt>Địa chỉ</dt><dd>${esc(mm.addressDetail || '—')}</dd></div>
        </dl>
        ${manager ? `<div class="card-danger-zone">
          <button class="btn btn-text danger" id="btnDelete"><span class="ms">delete</span>Xoá MM khỏi danh mục</button>
          <span class="hint-text">MM bị ẩn khỏi tìm kiếm và ô chọn; dữ liệu đã nhập giữ nguyên.</span>
        </div>` : ''}
      </div>
    </div>`;

  const reload = () => navigate(buildHash(`/middlemen/${mm.id}`));
  container.querySelector('#btnEdit')?.addEventListener('click', () => openEditDialog(mm, reload));

  // Doc/11a E2 — điều tra viên không sửa MM, nhưng báo sai được; người duyệt quyết.
  let proposals = [];
  const showProposals = async () => {
    proposals = await loadOpenProposals([mm.id]);
    if (!container.isConnected) return;
    renderOpenProposals(container.querySelector('#proposalsCard'), proposals, {
      titleOf: () => mm.name,
      onChanged: showProposals,
    });
  };
  void showProposals();
  container.querySelector('#btnReport').addEventListener('click', () =>
    openProposalSheet({
      entity: 'middleman',
      record: mm,
      title: `${mm.name} (${mm.code})`,
      link: buildHash(`/middlemen/${mm.id}`),
      openProposals: proposals,
      onSent: showProposals,
    }),
  );
  container.querySelector('#btnDelete')?.addEventListener('click', () => {
    openDeleteDialog({
      title: `Xoá MM "${mm.name}" (${mm.code}) khỏi danh mục?`,
      message: 'MM sẽ không còn hiện trong tìm kiếm và trong ô chọn MM của phiếu. Hồ sơ không bị xoá hẳn.',
      remove: (reason) => api.middlemen.remove(mm.id, reason),
      onDone: () => {
        toast('Đã xoá MM khỏi danh mục');
        navigate(buildHash('/middlemen'));
      },
    });
  });

  renderChangeLog(container.querySelector('#changeLogCard'), () => api.middlemen.changes(mm.id));
}

function openEditDialog(mm, onSaved) {
  let regionHost = null;
  openFormDialog({
    title: `Sửa hồ sơ MM: ${mm.name}`,
    bodyHtml: `
      <div class="form-grid">
        <div class="form-row"><label>Mã MM *</label><input name="code" value="${esc(mm.code)}" required maxlength="50"></div>
        <div class="form-row"><label>Tên *</label><input name="name" value="${esc(mm.name)}" required maxlength="200"></div>
        <div class="form-row"><label>Loại *</label><select name="type" required>${typeOptionsHtml(mm.type)}</select></div>
        <div id="editRegionHost" class="contents"></div>
        <div class="form-row"><label>Địa chỉ</label><input name="addressDetail" value="${esc(mm.addressDetail || '')}"></div>
        ${EDIT_REASON_FIELD_HTML}
      </div>`,
    onMount: (form) => {
      regionHost = form.querySelector('#editRegionHost');
      createRegionPicker(regionHost, { value: mm.regionId, required: true });
    },
    onSubmit: async (form) => {
      const regionId = pickedCommune(regionHost);
      const payload = {
        code: form.code.value.trim(),
        name: form.name.value.trim(),
        type: form.type.value,
        regionId,
        // Chuỗi rỗng = xoá địa chỉ: gửi null, bỏ trường đi thì máy chủ hiểu là "không đổi".
        addressDetail: form.addressDetail.value.trim() || null,
      };
      if (!payload.code || !payload.name) return 'Mã và tên MM không được để trống.';
      if (!regionId) return 'Chọn Tỉnh và Xã nơi MM đặt cơ sở.';
      const reasonError = editReasonError(form);
      if (reasonError) return reasonError;
      payload.reason = form.reason.value.trim();
      try {
        await api.middlemen.update(mm.id, payload);
      } catch (err) {
        if (err.code === 'CONFLICT' && err.existing?.id) {
          return { html: `${esc(err.message)} — <a href="${buildHash(`/middlemen/${err.existing.id}`)}">mở hồ sơ MM đó</a>` };
        }
        return err.message;
      }
      toast('Đã cập nhật hồ sơ MM');
      onSaved();
    },
  });
}
